import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";

import EmailInput from "src/Components/Authentication/EmailInput";
import PasswordInput from "src/Components/Authentication/PasswordInput";
import BlueButton from "src/Components/Authentication/BlueButton";
import GoogleButton from "src/Components/Authentication/GoogleButton";
import { validateEmail, API_URL } from "src/Common/function";

// The resend cooldown must survive a page reload, so the expiry time lives in
// localStorage and the countdown state is re-derived from it on mount.
const COOLDOWN_STORAGE_KEY = "forgotPasswordCooldownUntil";

const getRemainingCooldown = () => {
  const until = Number(localStorage.getItem(COOLDOWN_STORAGE_KEY));
  const remaining = Math.ceil((until - Date.now()) / 1000);
  return remaining > 0 ? remaining : 0;
};

const startCooldown = (seconds) => {
  localStorage.setItem(COOLDOWN_STORAGE_KEY, String(Date.now() + seconds * 1000));
};

function ForgotPassword() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [cooldown, setCooldown] = useState(getRemainingCooldown);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const backButtonClicked = () => {
    navigate("/");
  };

  const forgotButtonClicked = (e) => {
    if (isLoading || cooldown > 0) return;
    if (handleValidation()) {
      setIsLoading(true);
      axios
        .get(API_URL + "/api/auth/forgot-password/" + email.trim().toLowerCase())
        .then(function (response) {
          console.log(response);
          if (response.status == 200) {
            // The server answers the same whether or not the email has an
            // account, so don't claim an email was sent.
            toast("If an account exists for this email, we've sent a password reset link.");
            startCooldown(60);
            setCooldown(60);
          }
          setIsLoading(false);
        })
        .catch(function (error) {
          setIsLoading(false);
          // Surface the server message (e.g. the resend cooldown notice)
          // without crashing on a network error that has no response.
          toast(error.response?.data?.message || "An error occurred");
          // The backend rejected within its own cooldown window (e.g. the email
          // was requested from another tab or device) — mirror the remaining
          // wait locally so the button reflects it.
          if (error.response?.status === 429) {
            const match = /(\d+)\s*second/.exec(error.response?.data?.message || "");
            const seconds = match ? Number(match[1]) : 60;
            startCooldown(seconds);
            setCooldown(seconds);
          }
        });
    }
  };

  const handleValidation = () => {
    let errors = {};
    let formIsValid = true;

    if (!validateEmail(email)) {
      errors["email"] = "Email address is invalid";
      formIsValid = false;
    }

    setErrors(errors);
    console.log(errors);
    return formIsValid;
  };

  const emailChanged = (email) => {
    setEmail(email);
  };

  return (
    <div className="w-screen h-screen flex items-center justify-center">
      <div className="w-[332px] h-[411px] flex items-center justify-center">
        <div className="w-[296px] h-[400px]">
          <p className="font-OpenSansRegular text-[16px] text-titleColor text-center font-[700]">Forgot password?</p>
          <p className="font-OpenSansRegular text-[12px] text-subTitleColor text-center font-[600] mt-[5px]">
            Fill in your email and we'll send you a link to reset your password.
          </p>
          <div className="mt-[20px]" />
          <EmailInput onEmailChanged={emailChanged} />
          <BlueButton
            onButtonClicked={forgotButtonClicked}
            title={cooldown > 0 ? `RESEND IN ${cooldown}S` : "SEND EMAIL"}
            isLoading={isLoading}
            disabled={cooldown > 0}
          />
          <div className="mt-[5px]" />
          <p
            onClick={backButtonClicked}
            className="font-OpenSansRegular text-[10px] text-blueColor font-[400] text-center cursor-pointer"
          >
            Back to Login
          </p>

          <p className="font-OpenSansRegular text-[10px] text-subTitleColor text-center font-[400] mt-[20px]">
            By logging into the Makani Platform, you agree with our Privacy Policy and Terms of Use
          </p>
        </div>
      </div>
    </div>
  );
}

export default ForgotPassword;
